import React, { useState, useCallback } from 'react'
import { IcaCardRow } from '../IcaElements/IcaCardRow'
import { IcaCheckbox } from '../IcaElements/IcaCheckbox'
import { IcaMediaObject } from '../IcaElements/IcaMediaObject'
import { IcaMeta } from '../IcaElements/IcaMeta'
import { IcaInputTable } from '../IcaElements/IcaInputTable'
import { IcaButtonIcon } from '../IcaElements/IcaButtonIcon'
import GroceryFeedItemPopover from './GroceryFeedItemPopover'
import GroceryStockSettingsDialog from './GroceryStockSettingsDialog'
import { IcaMessageBox } from '../IcaElements/IcaMessageBox'
import ItemImage from '../ItemImage/ItemImage'
import Tag from '../Tag/Tag'
import ResetIcon from '../Icons/ResetIcon'
import '@ica-azure/ica-elements/IcaIconError'
import '@ica-azure/ica-elements/IcaIconTrash'
import '@ica-azure/ica-elements/IcaIconMoreVertical'
import { dateStringToRestockDisplayDate } from '../../utils/dateUtils'
import { stockText, getRunningOutThreshold } from '../../utils/stockUtils'
import { getTagTypeForStock } from '../Picklist/picklistHelpers'
import StoreGroceryStock from '../../types/StoreGroceryStock'
import { displayPrice } from '../../utils/displayPrice'

interface GroceryFeedTableRowProps {
  item: StoreGroceryStock
  even: boolean
  selectedArticles: string[]
  onSelectArticle: (ean: string) => void
  onCustomPriceChange: (item: StoreGroceryStock, newValue: number | null | undefined) => Promise<void>
  onCustomPriceDelete: (item: StoreGroceryStock) => Promise<void>
  onDeleteArticle: (item: StoreGroceryStock) => void
  onChangeStockSettings?: (item: StoreGroceryStock, threshold?: number, maxQuantity?: number) => Promise<void>
}

export default function GroceryFeedTableRow ({
  item,
  even,
  selectedArticles,
  onSelectArticle,
  onCustomPriceChange,
  onCustomPriceDelete,
  onDeleteArticle,
  onChangeStockSettings,
}: GroceryFeedTableRowProps) {
  const [inputErrors, setInputErrors] = useState<Record<string, string>>({})
  const [inputValues, setInputValues] = useState<Record<string, string>>({})
  const [popoverOpen, setPopoverOpen] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)

  const handleInputChange = useCallback((ean: string, value: string) => {
    setInputValues((prev) => ({ ...prev, [ean]: value }))
    // Clear error when user types
    setInputErrors((prev) => {
      const newErrors = { ...prev }
      delete newErrors[ean]
      return newErrors
    })
  }, [])

  const handleInputBlur = useCallback(async (item: StoreGroceryStock, value: string) => {
    const newValue = value !== '' ? Number(value) : null
  
    if (newValue !== null && (newValue < 1 || newValue > 99999)) {
      setInputErrors((prev) => ({ ...prev, [item.ean]: 'Priset måste vara mellan 1 och 99999 kr' }))
      return
    }
  
    // Clear any existing error
    setInputErrors((prev) => {
      const newErrors = { ...prev }
      delete newErrors[item.ean]
      return newErrors
    })
  
    // Save if valid
    if (newValue === null || newValue > 0) {
      try {
        await onCustomPriceChange(item, newValue)
      } finally {
        // Clear input value tracking only after save completes
        setInputValues((prev) => {
          const newValues = { ...prev }
          delete newValues[item.ean]
          return newValues
        })
      }
    }
  }, [onCustomPriceChange])

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.currentTarget.blur()
    }
  }

  const handleResetCustomPrice = useCallback((item: StoreGroceryStock) => {
    setInputValues((prev) => {
      const newValues = { ...prev }
      delete newValues[item.ean]
      return newValues
    })
    setInputErrors((prev) => {
      const newErrors = { ...prev }
      delete newErrors[item.ean]
      return newErrors
    })
    onCustomPriceDelete(item)
  }, [onCustomPriceDelete])

  return (
    <>
      <IcaCardRow odd={!even} noninteractive>
        <IcaCheckbox
          slot="left"
          id={`checkbox-${item.ean}`}
          checked={selectedArticles.includes(item.ean)}
          onChange={() => onSelectArticle(item.ean)}
        />

        <IcaMediaObject align="center">
          <ItemImage slot="first" ean={item.ean} />
          <div slot="last">
            <IcaMeta heading={item.name} subheading={item.ean} />
          </div>
        </IcaMediaObject>

        <p className="text-right pr-8">{displayPrice(item.ordinaryPrice)}</p>

        <div className="flex items-center pl-2">
          <div className="max-w-17">
            <IcaInputTable
              type="number"
              min={1}
              max={99999}
              value={inputValues[item.ean] ?? (displayPrice(item.customPrice as number) != null ? String(displayPrice(item.customPrice as number)) : '')}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange(item.ean, e.target.value)}
              onBlur={(e: React.FocusEvent<HTMLInputElement>) => handleInputBlur(item, e.target.value)}
              onKeyDown={handleInputKeyDown}
            />
          </div>
          {item.customPrice && (
            <div className="ml-3 tooltip">
              <div className="tooltip-text">Återställ till ordinarie pris.</div>
              <div
                className="grocery-feed__clickable"
                onClick={() => handleResetCustomPrice(item)}
              >
                <ResetIcon size="24px" color="var(--color-accent-red)" />
              </div>
            </div>
          )}
        </div>

        <p>{dateStringToRestockDisplayDate(item.restockedAt)}</p>

        <p>{`${item.restockedQuantity - item.quantity} st`}</p>

        <p>
          {item.maxQuantity != null ? (
            <>
              <span>{item.quantity}</span>
              <span className="text-[color:var(--color-grey-mid-light)]"> / {item.maxQuantity} st</span>
            </>
          ) : (
            <>{`${item.quantity} st`}</>
          )}
        </p>

        {item.runningOutThreshold ? 
          (<p><span className="text-[color:var(--color-grey-mid-light)]">Ändrat: </span>{`${item.runningOutThreshold} st`}</p>)
          : (<p>{`${getRunningOutThreshold(item.maxQuantity ?? item.restockedQuantity ?? 0)} st`}</p>)}

        <span className="pl-0">
          {(() => {
            const stockStatus = stockText({
              inStockQuantity: item.quantity,
              restockedQuantity: item.restockedQuantity,
              runningOutThreshold: item.runningOutThreshold,
            })
            if (!stockStatus) return null
            const tagType = getTagTypeForStock(stockStatus)
            return <Tag text={stockStatus} type={tagType} />
          })()}
        </span>

        <div className="ml-auto relative">
          <IcaButtonIcon accent onClick={() => setPopoverOpen(!popoverOpen)} chevron>
            <tgdb-ica-icon-more-vertical />
          </IcaButtonIcon>
          {popoverOpen && (
            <GroceryFeedItemPopover
              onEdit={() => setDialogOpen(true)}
              onDelete={() => onDeleteArticle(item)}
              onClose={() => setPopoverOpen(false)}
            />
          )}
        </div>
      </IcaCardRow>

      {dialogOpen && (
        <GroceryStockSettingsDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          onConfirm={async (threshold?: number, maxQty?: number) => {
            if (onChangeStockSettings) {
              await onChangeStockSettings(item, threshold, maxQty)
            }
          }}
          defaultRunningOutThreshold={item.runningOutThreshold}
          defaultMaxQuantity={item.maxQuantity}
          groceryItem={item}
        />
      )}

      {inputErrors[item.ean] && (
        <IcaMessageBox text={inputErrors[item.ean]} invalid>
          <tgdb-ica-icon-error slot="icon"></tgdb-ica-icon-error>
        </IcaMessageBox>
      )}
    </>
  )
}
